import {Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {Broadcast} from "../../model/broadcast/broadcast";
import {BroadcastsService} from "../../services/broadcasts.service";
import {ActivatedRoute} from "@angular/router";
import {Observable, Subscription} from "rxjs";
import {Control} from "../../model/broadcast/control";
import BroadcastJson from "../../../assets/broadcast-mock.json";
import {Game} from "../../model/broadcast/game";
import {GamesService} from "../../services/games.service";
import {League} from "../../model/league";
import {LeaguesService} from "../../services/leagues.service";
import {FirestoreKeepaliveService} from "../../services/firestore-keepalive.service";
import {take} from "rxjs/operators";
import {UtilService} from "../../services/util.service";

@Component({
    selector: 'app-broadcast-layer',
    templateUrl: './broadcast-layer.component.html',
    styleUrls: ['./broadcast-layer.component.scss'],
    standalone: false
})
export class BroadcastLayerComponent implements OnInit, OnDestroy, OnChanges {

  @Input() isSetup: boolean;
  @Input() previewControls: Control[];
  @Input() uid: string;

  broadcast: Broadcast;
  game: Game;
  league: League;
  gameSubs: Subscription;
  controls: Control[];
  subscriptions: Subscription[];
  singleComponent: string;

  // Liga, pro kterou je už skin vyřešen (drží i null výsledek), a liga, kterou právě
  // načítáme. Slouží k tomu, aby se grafika nevykreslila s výchozím skinem dřív, než se
  // načte skin z navázané ligy (viz skinReady).
  private resolvedLeagueId: string | null = null;
  private loadingLeagueId: string | null = null;

  fullscreenBroadcast = false;

  constructor(
    public broadcastService: BroadcastsService,
    public gamesService: GamesService,
    public leaguesService: LeaguesService,
    private keepalive: FirestoreKeepaliveService,
    private util: UtilService,
    private route: ActivatedRoute,
  ) {
    this.subscriptions = [];
    document.body.style.overflow = 'hidden';
  }

  // CSS třída skinu nasazená na #layer-container (přepíše CSS proměnné child komponent).
  get skinCssClass(): string {
    return this.util.getSkinDef(this.league?.skinId, this.league?.url).cssClass;
  }

  // Skin musí být znám PŘED prvním vykreslením. Když broadcast nemá ligu, skin je výchozí
  // (hned znám). Když ligu má, čekáme na její načtení – jinak by se grafika ukázala nejdřív
  // s výchozím skinem a po doskočení ligy skočila (falešná animace / skoky kompozice).
  get skinReady(): boolean {
    if (!this.broadcast) {
      return false;
    }
    if (!this.broadcast.leagueId) {
      return true;
    }
    return this.resolvedLeagueId === this.broadcast.leagueId;
  }

  // Zděděný skin z ligy zapíšeme do config (in-memory), aby skin-aware util metody
  // barev/log fungovaly bez protahování parametru přes všechny komponenty.
  private applySkin(): void {
    if (this.broadcast?.config) {
      this.broadcast.config.skinId = this.util.getSkinDef(this.league?.skinId, this.league?.url).id;
    }
  }

  // Načte ligu kvůli skinu (a dalším league-aware datům) hned, jakmile známe leagueId –
  // nezávisle na gameId. Guard zabrání duplicitnímu fetchi při častých emisích broadcastu.
  private loadLeagueForSkin(leagueId: string): void {
    if (this.resolvedLeagueId === leagueId || this.loadingLeagueId === leagueId) {
      return;
    }
    this.loadingLeagueId = leagueId;
    this.leaguesService.getLeague(leagueId).pipe(take(1)).subscribe(l => {
      this.league = l;
      this.resolvedLeagueId = leagueId;
      this.loadingLeagueId = null;
      this.applySkin();
    });
  }

  ngOnInit(): void {
    this.singleComponent = this.route.snapshot.paramMap.get('component');
    if (!this.uid) {
      this.uid = this.route.snapshot.paramMap.get('uid');
    }
    if (!this.uid) {
      const tmp = BroadcastJson;
      //this.broadcast = tmp as Broadcast;
      this.controls = this.previewControls;
      return;
    }

    this.subscribeToBroadcast(this.uid);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.uid?.currentValue != changes.uid?.previousValue) {
      this.subscribeToBroadcast(changes.uid.currentValue);
    }
  }

  private subscribeToGame(broadcast: Broadcast): void {
    if (this.gameSubs) {
      this.gameSubs.unsubscribe();
    }
    this.game = {
      gameId: broadcast.gameId,
      home: {
        shortName: broadcast.config.homeConfig.shortName,
        fullName: broadcast.config.homeConfig.fullName,
        runs: 0,
        hits: 0,
        errors: 0,
      },
      away: {
        shortName: broadcast.config.homeConfig.shortName,
        fullName: broadcast.config.homeConfig.fullName,
        runs: 0,
        hits: 0,
        errors: 0,
      },
      balls: 0,
      strikes: 0,
      outs: 0,
      inning: 1,
      inningPart: 'S',
      lastUpdate: broadcast.scheduled,
      tournamentId: broadcast.leagueId,
    };
    this.gameSubs = this.gamesService.getGame(broadcast.gameId).subscribe(game => {
      this.keepalive.notifyActivity();
      if (!this.isSetup && (this.singleComponent == 'status' || this.singleComponent == 'main' || !this.singleComponent)) {
        if (this.game && this.broadcast?.config?.autopilot) {
          this.broadcastService.detectChanges(this.broadcast, game, this.game);
        }
        if (this.game && !this.broadcast.leagueId || this.league?.poweredBy) {
          this.broadcastService.detectPowerBy(this.broadcast, game, this.game);
        }
      }
      this.game = game;
    });
  }

  private subscribeToBroadcast(uid: string): void {
    this.subscriptions.forEach(s => {
      s.unsubscribe();
    });
    this.subscriptions = [];
    this.keepalive.start();
    this.resolvedLeagueId = null;
    this.loadingLeagueId = null;
    const broadcastSubscription = this.broadcastService.getBroadcast(uid).subscribe(broadcast => {
      this.keepalive.notifyActivity();
      // Ligu načteme kvůli skinu hned jak známe leagueId (nezávisle na gameId), aby byl
      // skin vyřešen před prvním vykreslením (viz skinReady) a grafika neskákala.
      if (broadcast?.leagueId) {
        this.loadLeagueForSkin(broadcast.leagueId);
      }
      //if there is no game loaded or gameId has changed subscribe to a game
      if (broadcast?.gameId && (this.game?.gameId != broadcast?.gameId || this.broadcast.config.autopilot != broadcast.config.autopilot)) {
        this.subscribeToGame(broadcast);
      }

      this.broadcast = broadcast;
      this.applySkin();
    });
    this.subscriptions.push(broadcastSubscription);

    const controlsSubs = this.broadcastService.getControls(this.uid).subscribe(controls => {
      this.keepalive.notifyActivity();
      this.controls = controls;
    });
    this.subscriptions.push(controlsSubs);
    const fullScreenSubscription = this.route.data.subscribe((data) => {
      this.fullscreenBroadcast = data.fullscreen;
    });
    this.subscriptions.push(fullScreenSubscription);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => {
      sub.unsubscribe();
    });
    if (this.gameSubs) {
      this.gameSubs.unsubscribe();
    }
    this.keepalive.stop();
  }
}
